// Classes CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSchoolContext } from './school-context'
import type { Class } from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

export interface ClassFilters {
  school_id?: string
  includeInactive?: boolean
}

// Get all classes for a school (active only by default)
export async function getClassesDirect(schoolId?: string, filters: ClassFilters = {}): Promise<Class[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolContext = getSchoolContext()
    const effectiveSchoolId = schoolId || filters.school_id || schoolContext

    if (!effectiveSchoolId) {
      throw new Error('School ID is required')
    }

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('school_id', `eq.${effectiveSchoolId}`)
    if (!filters.includeInactive) {
      params.append('is_active', 'eq.true')
    }
    params.append('order', 'class_name.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/classes?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch classes')
    }

    return await response.json()
  })
}

// Create a new class
export async function createClassDirect(data: {
  school_id: string
  class_name: string
  class_group: string
}): Promise<Class> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/classes`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create class')
    }

    const result: Class[] = await response.json()
    return result[0]
  })
}

// Update a class
export async function updateClassDirect(
  id: string,
  updates: { class_name?: string; class_group?: string }
): Promise<Class> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/classes?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update class')
    }

    const result: Class[] = await response.json()
    if (!result[0]) throw new Error('Class not found')
    return result[0]
  })
}

// Delete a class: soft-delete if timetable records exist, hard-delete otherwise
export async function deleteClassDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check for existing timetable records via class_sections
    const checkParams = new URLSearchParams()
    checkParams.append('select', 'id')
    checkParams.append('class_section_id', `in.(select id from class_sections where class_id=eq.${id})`)
    checkParams.append('limit', '1')

    const checkResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?class_section_id=in.(select+id+from+class_sections+where+class_id=eq.${id})&select=id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    const hasTimetableRecords = checkResp.ok && ((await checkResp.json()) as any[]).length > 0

    if (hasTimetableRecords) {
      // Soft-delete: set is_active = false
      const softResp = await fetch(`${SUPABASE_URL}/rest/v1/classes?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: false }),
      })
      if (!softResp.ok) {
        const error = await softResp.json()
        throw new Error(error.message || 'Failed to deactivate class')
      }
    } else {
      // Hard-delete
      const response = await fetch(`${SUPABASE_URL}/rest/v1/classes?id=eq.${id}`, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete class. It may have existing sections.')
      }
    }
  })
}

// Reactivate a soft-deleted class
export async function reactivateClassDirect(id: string): Promise<Class> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/classes?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ is_active: true }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to reactivate class')
    }

    const result: Class[] = await response.json()
    if (!result[0]) throw new Error('Class not found')
    return result[0]
  })
}
